<template>
  <div class="gift-detail">
    <nav class="gift-detail__back">
      <router-link :to="{ name: 'GiftList' }">&#60; zpět</router-link>
    </nav>

    <div class="gift-detail__content">
      <img :src="gift.image300xauto"
           :alt="`Obrázek dárku ${gift.name}`"
           loading="lazy"
           class="gift-detail__image"/>

      <div class="gift-detail__info">
        <h1>{{ gift.name }}</h1>
        <p>{{ gift.description }}</p>
        <a :href="gift.link"
           :style="{ backgroundImage: `url(${caranecEshop})` }"
           class="gift-detail__eshop-link"
           target="_blank">e-shop</a>
        <p v-if="gift.booked" class="gift-detail__booked">Zamluveno</p>
        <div v-else class="gift-detail__book">
          <div class="gift-detail__input">
            <input v-model="email" id="input-book" type="email" placeholder="email"/>
            <p v-if="validationMessage">{{ validationMessage }}</p>
          </div>
          <label class="gift-detail__button" for="input-book">
            <Button @click.native="bookGift" variant="green">Zamluvit</Button>
          </label>
        </div>
      </div>

      <Loader v-if="loader"/>
    </div>

    <ModalWindow v-if="showSuccessMessage" v-model="showSuccessMessage">
      <template v-slot:title>Dárek úspěšně zamluven</template>
      <template v-slot:text>
        Nyní by ti měl přijít email na adresu {{ email }} s potvrzením zamluvení.
        Pokud email nepřijde do 10 minut, tak se prosím koukni do spamu (bohužel tam občas skončí) a pokud nebude ani tam,
        tak napiš na <a href="mailto:svatba@bachrony.com">svatba@bachrony.com</a> a Franta to vyřeší.
      </template>
      <template v-slot:buttons>
        <Button @click.native="showSuccessMessage = false" variant="grey">Zavřít</Button>
      </template>
    </ModalWindow>
  </div>
</template>

<script>
import Loader from '@/components/ui/Loader.vue';
import Button from '@/components/ui/Button.vue';
import ModalWindow from '@/components/ui/ModalWindow.vue';
import {
  fetchGift,
  sendEmail,
  reportError,
  bookGift,
} from '@/services';
import caranecEshop from '@/assets/caranec-eshop.png';
import emailTemplate from '@/constants/emailTemplate';

export default {
  name: 'GiftDetail',
  components: {
    Loader,
    Button,
    ModalWindow,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      gift: {},
      email: '',
      validationMessage: '',
      loader: false,
      caranecEshop,
      showSuccessMessage: false,
    };
  },
  created() {
    this.updateGift();
  },
  methods: {
    async updateGift() {
      const gift = await fetchGift(this.id).catch((error) => console.error(error));

      if (gift) {
        this.gift = gift;
      }
    },
    validateEmail() {
      // eslint-disable-next-line max-len
      const regexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const result = regexp.test(String(this.email).toLowerCase());

      if (this.email === '') {
        this.validationMessage = 'Pro zamluvení musí být vyplněn e-mail';
      } else if (!result) {
        this.validationMessage = 'Tento e-mail není validní.';
      } else {
        this.validationMessage = '';
      }

      return result;
    },
    async bookGift() {
      if (this.validateEmail()) {
        this.validationMessage = '';
        this.loader = true;
        await bookGift(this.id, this.email).then(async (unbookCode) => {
          const { html, text } = emailTemplate(this.id, this.gift.name, this.gift.link, unbookCode);

          await sendEmail({
            to: this.email,
            subject: 'Dárek úspěšně zamluven! 👍🏻',
            text,
            html,
          }).then(() => {
            this.updateGift();
            this.showSuccessMessage = true;
          }).catch((error) => {
            reportError(error);
            this.validationMessage = 'Zamluvení se nepodařilo. Problém byl nahlášen Frantovi a bude to řešit 🤓.';
          });
        }).catch((error) => {
          reportError(error);
          this.validationMessage = 'Zamluvení se nepodařilo. Problém byl nahlášen Frantovi a bude to řešit 🤓.';
        });
        this.loader = false;
      }
    },
  },
  watch: {
    email() {
      this.validateEmail(this.email);
    },
  },
};
</script>

<style lang="scss" scoped>
  .gift-detail {
    padding: 0 20px;

    @media (max-width: 780px) {
      padding: 0;
    }

    &__content {
      background-color: #fff;
      display: flex;
      padding: 40px 60px;
      position: relative;
      border-radius: 6px;

      @media (max-width: 780px) {
        flex-direction: column;
        border-radius: 0;
      }

      @media (max-width: 464px) {
        padding: 40px 30px;
      }
    }

    &__image {
      width: 300px;
      margin-right: 70px;

      @media (max-width: 780px) {
        margin: 0 auto;
        margin-bottom: 10px;
      }

      @media (max-width: 464px) {
        width: 200px;
      }

      & img {
        width: 100%;
        height: auto;
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;

      @media (max-width: 780px) {
        align-items: center;
      }

      & h1 {
        font-weight: 100;
        border-bottom: 2px solid;
        margin: 0;
        padding-bottom: 10px;

        @media (max-width: 464px) {
          font-size: 1.3rem;
        }
      }

      & p {
        font-weight: 100;
        padding: 12px 0;
      }
    }

    &__eshop-link {
      color: white;
      background-position: center;
      background-size: 100%;
      background-repeat: no-repeat;
      width: 134px;
      height: 27px;
      display: flex;
      font-weight: 100;
      padding-left: 34px;
      box-sizing: border-box;
    }

     &__book {
      padding-top: 12px;
      display: flex;
      flex-wrap: wrap;
      width: 100%;

      @media (max-width: 780px) {
        flex-direction: column;
        align-items: center;
      }
    }

    &__button {
      @media (max-width: 780px) {
        margin-bottom: 10px;
        width: 300px;
      }

      @media (max-width: 464px) {
        margin-bottom: 10px;
        width: 200px;
      }
    }

    &__input {
      margin: 0 10px 10px 0;
      width: 300px;

      @media (max-width: 780px) {
        margin: 0 0 10px;
      }

      @media (max-width: 464px) {
        width: 200px;
      }

      & p {
        margin: 0;
        padding: 0;
        color: red;
        font-size: 0.7rem;
      }

      & input {
        border: 1px solid grey;
        border-radius: 2px;
        padding: 6px 4px;
        text-align: center;
        font-size: 0.9rem;
        box-sizing: border-box;
        width: 100%;

        @media (max-width: 780px) {
          width: 100%;
        }
      }

      & p {
        width: 100%;
      }
    }

    &__back {
      padding: 10px 0;
      font-size: 1.2rem;
      font-weight: 100;

      @media (max-width: 780px) {
        padding: 10px 20px;
      }
    }

    &__booked {
      font-size: 1.7rem;
      color: red;
    }
  }
</style>
